import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { urlSyncEffect } from "recoil-sync";
import { number, string } from "@recoiljs/refine";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { axios } from "../lib/request";
import { teamDetailQueryKey, teamsQueryKey } from "../lib/refresh";
import { useItemQuery } from "../Global.state";
import { INFINITE_QUERY_KEY, MAX_PER_PAGE } from "../consts/request";
import { useEffect } from "react";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../lib/error";

export const deleteIdState = atom({
  key: "teams/deleteId",
  default: 0,
});

export const notificationState = atom({
  key: "notification",
  default: {},
});

export const pageState = atom({
  key: "teams/page",
  default: 1,
  effects: [
    urlSyncEffect({
      refine: number(),
      history: 'push'
    })
  ],
});

export const pageSizeState = atom({
  key: "teams/pageSize",
  default: MAX_PER_PAGE,
  effects: [
    urlSyncEffect({
      refine: number(),
      history: 'push'
    })
  ],
});

export const teamsTermState = atom({
  key: "teams/term",
  default: '',
  effects: [
    urlSyncEffect({
      refine: string(),
      history: 'push'
    })
  ],
});

export const teamsIdState = atom({
  key: "teams/id",
  default: 0,
  effects: [
    urlSyncEffect({
      refine: number(),
      history: 'push'
    })
  ],
});

/**
 * Fetches the paginated list of teams for a specific game.
 * 
 * @param {string} game_id - The ID of the game.
 * @param {number} page - The current page number.
 * @param {number} per_page - The number of items per page.
 * @returns {Promise<Object>} The teams payload.
 */
export const fetchTeams = async (game_id, page, per_page) => {
  if (game_id === "new") {
    return { teams: [] };
  }

  const response = await axios({
    url: process.env.REACT_APP_API_TEAM_RETRIEVE_URL,
    params: { game_id, page, per_page },
  });

  return response?.data || { teams: [] };
};

export const fetchTeamDetail = async (id) => {
  const response = await axios({
    url: `${process.env.REACT_APP_API_PLAYER_URL}/${id}/players.json`,
  });

  return response.data;
};

export const fetchFilteredTeams = async (game_id, page, per_page, term) => {
  const response = await axios({
    url: process.env.REACT_APP_API_FILTERD_TEAM_RETRIEVE_URL,
    params: { game_id, page, per_page, term },
  });

  return response.data;
};

export const fetchPlayers = async (game_id, page = 1, per_page = MAX_PER_PAGE) => {
  return fetchTeams(game_id, page, per_page);
};

export const updateTeam = async (game_id, id, params) => {
  const url = `${process.env.REACT_APP_API_TEAM_UPDATE_URL}/${id}.json`;
  return await axios.put(url, { ...params, game_id });
};

export const useInfiniteTeamsQuery = () => {
    const { game_id } = useParams();
  
    return useInfiniteQuery(
      teamsQueryKey(game_id, 1, INFINITE_QUERY_KEY),
      ({ pageParam }) => fetchPlayers(game_id, pageParam, MAX_PER_PAGE),
      {
        getNextPageParam: (lastPage, pages) => {
          const { count } = lastPage || {};
          const loaded = pages.reduce(
            (sum, page) => (sum += page?.teams.length),
            0
          );
  
          if ((lastPage && (lastPage?.teams || []).length === 0) || count <= loaded) {
            return undefined;
          }
    
          return Number(lastPage?.page || 0) + 1;
        },
        staleTime: Infinity
      }
    )
};

export const useOptionQueryKey = (game_id, page, pageSize, term) => {
    return teamsQueryKey(game_id, term, page, pageSize);
};

// teams data used in dropdown options
export const useTeamsOptionsQuery = ({ term = '', page }) => {
    const { game_id } = useParams();
    const pageSize = MAX_PER_PAGE;
    const queryKey = useOptionQueryKey(game_id, page, pageSize, term)
  
    return useQuery(
      queryKey,
      () => fetchFilteredTeams(game_id, page, pageSize, term),
      { 
          keepPreviousData: false, 
          suspense: false,
      }
    );
};

// infinite teams options query key, to differentiate from the infinite teams query key
// because the infinite teams query key is used to invalidate the infinite teams query
// and the infinite teams options query key is used to invalidate the infinite teams options query
export const infiniteTeamsOptionsQueryKey = (game_id, term = '') => {
  return ['teams-options', game_id, INFINITE_QUERY_KEY, term]
}

export const infinitePlayersOptionsQueryKey = (game_id, term = '') => {
  return ['players-options', game_id, INFINITE_QUERY_KEY, term]
}

export const useTeamsOptionsInfiniteQuery = ({ term }) => {
    const { game_id } = useParams();
    const pageSize = MAX_PER_PAGE;
  
    return useInfiniteQuery({
      queryKey: infiniteTeamsOptionsQueryKey(game_id, term),
      queryFn: ({ pageParam: page }) => fetchFilteredTeams(game_id, term ? 1 : page, pageSize, term),
      getNextPageParam: (lastPage) => {
        const { page, pages: _pages } = lastPage
        const cursor = parseInt(page)
        const pages = parseInt(_pages)
        if (cursor < pages && !term) {
          return cursor + 1
        }
      },
    })
};

export const useTeamDetailQuery = (id) => {
  return useQuery(
    teamDetailQueryKey(id), 
    () => fetchTeamDetail(id), 
    {
        suspense: false,
    }
  );
};

/**
 * A React Query hook that fetches teams data for the current game,
 * respecting pagination and search term states.
 * 
 * @param {string} game_id - The ID of the game.
 * @returns {Object} The React Query result object.
 */
export const useTeamsDataQuery = (game_id) => {
  const page = useRecoilValue(pageState);
  const pageSize = useRecoilValue(pageSizeState);
  const term = useRecoilValue(teamsTermState);
  const queryKey = useQueryKey(game_id, term);
  const setAlert = useSetRecoilState(alertState);
  return useQuery(queryKey, () => fetchFilteredTeams(game_id, page, pageSize, term), {
    suspense: true,
    useErrorBoundary: false,
    onError: (error) => {
      const message = getErrorMessage(error);
      setAlert({ type: "error", message, closable: true });
    },
  });
};

const useQueryKey = (game_id, term = '') => {
  const page = useRecoilValue(pageState);
  const pageSize = useRecoilValue(pageSizeState);
  return teamsQueryKey(game_id, page, pageSize, term);
};

/**
 * A custom hook that extracts the teams array from the `useTeamsDataQuery` result.
 * 
 * @param {string} game_id - The ID of the game.
 * @returns {Array<Object>} The array of team objects.
 */
export const useTeamsQuery = (game_id) => {
  const { data } = useTeamsDataQuery(game_id);
  return data?.teams;
};

const getTeamActionContext = ({ id, item }) => {
  return item?.name ? `saving team "${item.name}" (ID ${id})` : `saving team ID ${id}`;
};

export const useTeamQuery = (game_id, id) => {
  const { data, isFetching, isLoading } = useTeamsDataQuery(game_id);
  const queryKey = useQueryKey(game_id);

  return [
    ...useItemQuery(data, id, "teams", queryKey, (params) =>
      updateTeam(game_id, id, params),
      { actionContext: getTeamActionContext }
    ),
    isFetching,
    isLoading,
  ];
};

export const useAllPlayersQuery = () => {
  const { game_id } = useParams();

  const { 
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    teamsQueryKey(game_id, 1, INFINITE_QUERY_KEY),
    ({ pageParam }) => fetchPlayers(game_id, pageParam, MAX_PER_PAGE),
    {
      getNextPageParam: (lastPage, pages) => {
        const { count } = lastPage || {};
        const loaded = pages.reduce(
          (sum, page) => (sum += page?.teams?.length),
          0
        );

        if ((lastPage && (lastPage?.teams || []).length === 0) || count <= loaded) {
          return undefined;
        }
  
        return Number(lastPage?.page || 0) + 1;
      },
      staleTime: Infinity
    }
  );
  
  const playerData = (data?.pages || []).reduce((result, page) => {
    return {
      teams: [
        ...result.teams || [],
        ...page?.teams || [],
      ],
      unassigned_users: [
        ...result.unassigned_users || [],
        ...page?.unassigned_users || [],
      ].filter(user => {
        return !result?.unassigned_users?.find(u => u.id === user.id)
      })
    }
  }, {
    teams: [],
    unassigned_users: [],
  })
  const nextPage = [...data?.pageParams || []].pop()

  return {
    nextPage,
    playerData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

export const useInfinitePlayers = () => {
  const {
    nextPage,
    playerData: data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAllPlayersQuery()
  useEffect(() => {
    let loaded = false
    if (!loaded && hasNextPage) {
      fetchNextPage()
    }
    return () => {
      loaded = true
    }
  }, [
    nextPage,
    hasNextPage,
    fetchNextPage,
  ])
  const players = data?.unassigned_users?.slice();

  for (let team of data.teams) {
    for (let player of team.users) {
      if (!players.find((p) => p.id === player.id)) {
        if (player.email === undefined) {
          player.email = ''
        }
        players.push(player);
      }
    }
  }

  const result = players.sort((a, b) => {
    const aName = a.name || a.email;
    const bName = b.name || b.email;

    if (aName === bName) {
      return 0;
    }

    return aName < bName ? -1 : 1;
  });

  return {
    isLoading: isFetchingNextPage,
    players: result,
    teams: data?.teams,
    administrators: result.filter((player) => player.game.administrator),
    banned: result.filter((player) => player.game.banned),
    coordinators: result.filter((player) => player.game.coordinator),
  }
}

export const useTeamsFetchingQuery = () => {
  const { game_id } = useParams();
  const { isFetching, isRefetching } = useTeamsDataQuery(game_id);
  return isFetching || isRefetching;
};

export const useTeamsPagesQuery = () => {
  const { game_id } = useParams();
  const { data } = useTeamsDataQuery(game_id);
  return data?.pages;
};

export const useUnassignedQuery = (game_id) => {
  const { data } = useTeamsDataQuery(game_id);
  return data?.unassigned_users;
};

export const useTeamPhotoThumbQuery = (id) => {
  const { game_id } = useParams();
  const [team] = useTeamQuery(game_id, id);
  const photo = team.assets.photos.find(
    (photo) => photo.name === "standard" && photo.url
  );

  return photo?.url;
};

export const useCreateMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(game_id);
  const { data } = useTeamsDataQuery(game_id);
  const pageSize = useRecoilValue(pageSizeState);

  const { isLoading, mutateAsync } = useMutation(
    () => axios.post(process.env.REACT_APP_API_TEAM_CREATE_URL, { game_id, per_page: pageSize }),
    {
      onSuccess: ({ data: team }) => {
        const teams = [team.team, ...data.teams];
        const uri = teamsQueryKey(game_id);

        queryClient.invalidateQueries(uri);

        teams.sort((a, b) => {
          const first = a.name.toLocaleLowerCase();
          const second = b.name.toLocaleLowerCase();

          if (first === second) {
            return 0;
          }

          if (first < second) {
            return -1;
          }

          return 1;
        });

        queryClient.setQueryData(queryKey, {
          ...data,
          teams,
        });
      },
    }
  );

  return { isCreating: isLoading, create: mutateAsync };
};

export const useDestroyMutation = () => {
  const { game_id } = useParams();
  const [deleteId, setDeleteId] = useRecoilState(deleteIdState);
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(game_id);

  const { isLoading, mutate } = useMutation(
    () =>
      axios.delete(
        `${process.env.REACT_APP_API_TEAM_DELETE_URL}/${deleteId}.json?game_id=${game_id}`
      ),
    {
      onSettled: () => setDeleteId(0),
      onSuccess: () => queryClient.invalidateQueries(teamsQueryKey(game_id)),
      onMutate: () => {
        const data = queryClient.getQueryData(queryKey);
        console.log('data', data);
        const teams = data.teams.filter((team) => team.id !== deleteId);

        queryClient.setQueryData(queryKey, { ...data, teams });
      },
    }
  );

  return { isDeleting: isLoading, destroy: mutate };
};

export const useAddAdministratorMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['administrators', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.post(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/administrator.json?player_id=${id}`
      ),
    {
      onMutate: (user) => {
        const { id, email } = user;
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            administrators: [...(previousData?.administrators || []), { user_id: id, email }],
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { addAdministrator: mutateAsync };
};

export const useRemoveAdministratorMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['administrators', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.delete(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/administrator.json?player_id=${id}`
      ),
    {
      onMutate: ({ id }) => {
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            administrators: (previousData?.administrators || []).filter(admin => admin.user_id !== id),
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { removeAdministrator: mutateAsync };
};

export const useAddBanMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['banned', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.post(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/ban.json?player_id=${id}`
      ),
    {
      onMutate: (user) => {
        const { id, email } = user;
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            bans: [...(previousData?.bans || []), { user_id: id, email }],
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { addBan: mutateAsync };
};

export const useRemoveBanMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['banned', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.delete(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/ban.json?player_id=${id}`
      ),
    {
      onMutate: ({ id }) => {
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            bans: (previousData?.bans || []).filter(
              (banned) => banned?.user_id !== id
            ),
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { removeBan: mutateAsync };
};

export const useAddCoordinatorMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['coordinators', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.post(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/coordinator.json?player_id=${id}`
      ),
    {
      onMutate: (user) => {
        const { id, email } = user;
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            coordinators: [...(previousData?.coordinators || []), { user_id: id, email }]
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { addCoordinator: mutateAsync };
};

export const useRemoveCoordinatorMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const key = ['coordinators', game_id];

  const { mutateAsync } = useMutation(
    ({ id }) =>
      axios.delete(
        `${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/coordinator.json?player_id=${id}`
      ),
    {
      onMutate: ({ id }) => {
        const previousData = queryClient.getQueryData(key) || {};
        queryClient.cancelQueries(key);
        queryClient.setQueryData(
          key,
          {
            ...previousData,
            coordinators: (previousData?.coordinators || []).filter(coordinator => coordinator.user_id !== id),
          }
        );
      },
      onSuccess: () => queryClient.invalidateQueries(key),
    }
  );

  return { removeCoordinator: mutateAsync };
};

export const useAdministratorsQuery = () => {
  const { game_id } = useParams();
  return useQuery(
    ['administrators', game_id],
    () => axios.get(`${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/administrator.json`)
      .then(response => response.data),
    {
      select: (data) => {
        // Map user_id to id for compatibility with form components
        return (data?.administrators || []).map(admin => ({ ...admin, id: admin.user_id }));
      }
    }
  );
};

export const useCoordinatorsQuery = () => {
  const { game_id } = useParams();
  return useQuery(
    ['coordinators', game_id], 
    () => axios.get(`${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/coordinator.json`)
      .then(response => response.data),
    {
      select: (data) => {
        // Map user_id to id for compatibility with form components
        return (data?.coordinators || []).map(coordinator => ({ ...coordinator, id: coordinator.user_id }));
      }
    }
  );
};

export const useBannedQuery = () => {
  const { game_id } = useParams();
  return useQuery(
    ['banned', game_id],
    () => axios.get(`${process.env.REACT_APP_API_GAME_ACCESS_URL}/${game_id}/ban.json`)
      .then(response => response.data),
    {
      select: (data) => {
        // Map user_id to id for compatibility with form components
        return (data?.bans || []).map(banned => ({ ...banned, id: banned.user_id }));
      }
    }
  );
};

export const useInfinitePlayersOptionsQuery = ({ term }) => {
  const { game_id } = useParams();
  const pageSize = MAX_PER_PAGE;

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: infinitePlayersOptionsQueryKey(game_id, term),
    queryFn: ({ pageParam: page }) => fetchFilteredTeams(game_id, term ? 1 : page, pageSize, term),
    getNextPageParam: (lastPage) => {
      const { page, pages: _pages } = lastPage;
      const cursor = parseInt(page);
      const pages = parseInt(_pages);
      if (cursor < pages && !term) {
        return cursor + 1;
      }
    },
  });

  const players = data?.pages.flatMap(page => page.unassigned_users) || [];

  for (let team of data?.pages.flatMap(page => page.teams) || []) {
    for (let player of team.users) {
      if (!players.find((p) => p.id === player.id)) {
        if (player.email === undefined) {
          player.email = ''
        }
        players.push(player);
      }
    }
  }

  return {
    isLoading: isFetchingNextPage,
    players,
    teams: data?.pages.flatMap(page => page.teams) || [],
    fetchNextPage,
    isFetching: isFetching || isFetchingNextPage,
    hasNextPage,
  };
};
